import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["dropzone", "imagesContainer", "image", "grid", "columnsSelect"]
  static values = {
    id: Number,
    postId: Number,
    uploadUrl: String
  }

  connect() {
    this.initializeSortable()
  }

  initializeSortable() {
    if (this.hasGridTarget) {
      this.sortable = Sortable.create(this.gridTarget, {
        animation: 150,
        ghostClass: 'image-ghost',
        filter: '.image-dropzone',
        onEnd: this.handleImageReorder.bind(this)
      })
    }
  }

  handleDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add('drag-over')
  }

  handleDragLeave(event) {
    this.dropzoneTarget.classList.remove('drag-over')
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('drag-over')

    const files = Array.from(event.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    )

    if (files.length > 0) {
      this.uploadImages(files)
    }
  }

  uploadImages(files) {
    files.forEach(file => {
      const upload = new DirectUpload(
        file,
        this.uploadUrlValue || '/rails/active_storage/direct_uploads'
      )

      upload.create((error, blob) => {
        if (error) {
          console.error('Upload error:', error)
        } else {
          this.createBlockImage(blob.signed_id)
        }
      })
    })
  }

  createBlockImage(signedId) {
    const formData = new FormData()
    formData.append('block_image[file]', signedId)

    fetch(`/admin/posts/${this.postIdValue}/blocks/${this.idValue}/images`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: formData
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error creating image:', error)
    })
  }

  handleImageReorder(event) {
    const imageIds = this.imageTargets.map(img => img.dataset.imageId)

    fetch(`/admin/posts/${this.postIdValue}/blocks/${this.idValue}/images/reorder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ image_ids: imageIds })
    })
  }

  editCaption(event) {
    const imageId = event.currentTarget.dataset.imageId
    const viewCaption = event.currentTarget
    const editCaption = viewCaption.nextElementSibling

    viewCaption.classList.add('d-none')
    editCaption.classList.remove('d-none')
    editCaption.querySelector('input').focus()
  }

  saveCaption(event) {
    const imageId = event.currentTarget.dataset.imageId
    const input = event.currentTarget
    const caption = input.value

    fetch(`/admin/posts/${this.postIdValue}/blocks/${this.idValue}/images/${imageId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ block_image: { caption: caption } })
    })
    .then(() => {
      const editCaption = input.closest('.caption-edit')
      const viewCaption = editCaption.previousElementSibling

      viewCaption.textContent = caption || 'Click to add caption...'
      viewCaption.classList.remove('d-none')
      editCaption.classList.add('d-none')
    })
  }

  cancelCaption(event) {
    const editCaption = event.currentTarget.closest('.caption-edit')
    const viewCaption = editCaption.previousElementSibling

    viewCaption.classList.remove('d-none')
    editCaption.classList.add('d-none')
  }

  deleteImage(event) {
    event.preventDefault()
    const imageElement = event.currentTarget.closest('[data-image-block-target="image"]')
    const imageId = imageElement.dataset.imageId

    if (confirm('Delete this image?')) {
      fetch(`/admin/posts/${this.postIdValue}/blocks/${this.idValue}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      .then(() => {
        imageElement.remove()
      })
    }
  }

  updateColumns(event) {
    const columns = parseInt(event.target.value)

    // Update the grid CSS immediately for visual feedback
    if (this.hasGridTarget) {
      this.gridTarget.style.setProperty('--grid-columns', columns)
      this.gridTarget.dataset.columns = columns
    }

    // Save to backend
    fetch(`/admin/posts/${this.postIdValue}/blocks/${this.idValue}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ block: { columns: columns } })
    })
  }
}
